import { defineStore } from 'pinia'
import { ref, computed, toRefs } from 'vue'
import useFeatures from './features'
import { Attachment } from '@/services/attachments.service'
import { Drawing, Orthophoto } from '@/interfaces/misc.interface'
import {
  LineCategoryDto,
  Line,
  LineGeoJsonFeature,
  LineGeoJsonProperties
} from '@/services/lines.service'
import {
  addLineFeaturesForType,
  setExistingLineDataVisibility,
  setNewLineDataVisibility
} from '@/utils/line'
import { Ticket } from '@/services/tickets.service'
import { ZoneProperties } from '@/services/zones.service'

type OldFeatureType =
  | 'featureInfo'
  | 'featureAttachments'
  | 'featureAnalytic'
  | 'featureProjectAnalysis'
  | 'featureWip'
  | ''

export const useGeneralStore = defineStore('general', () => {
  const features = useFeatures()
  const { setMainFeature } = features
  const { mainFeatureName } = toRefs(features)
  // States
  const currentOverlayModalPinia = ref<string>('')
  const currentOverlayModalOnModalPinia = ref<string>('') // Modal on modal was a joint decision on prod/tech review 2024-08-19
  const filesList = ref<Attachment[]>([])
  const filesUploaded = ref<string[]>([])
  const filteredDrawings = ref<Drawing[]>([])
  const filteredLineLayers = ref<Line[]>([])
  const filteredOrthophotos = ref<Orthophoto[]>([])
  const filteredWmsLayers = ref<any[]>([])
  const isFeatureFullscreen = ref<boolean>(false)
  const linesForSelectedRealEstate = ref<LineGeoJsonFeature[]>([])
  const lineCategoriesForSelectedRealEstate = ref<LineCategoryDto[]>([])
  const linksList = ref<Attachment[]>([])
  const linksUpdated = ref<string[]>([])
  const rightClickCoordinate = ref<number[]>([0, 0])
  const selectedAnalysisKPI = ref<string | undefined>(undefined)
  const selectedFile = ref<Attachment | null>(null)
  const selectedLine = ref<LineGeoJsonProperties | null>(null)
  const selectedLineLayerId = ref<string | null>(null)
  const selectedLink = ref<Attachment | null>(null)
  const selectedTicket = ref<Ticket | null>(null)
  const selectedZone = ref<ZoneProperties | null>(null)
  const selectedZoneLayerId = ref<string | null>(null)
  const showLineMenu = ref<boolean>(false)
  const showOverlayModalPinia = ref<boolean>(false)
  const showOverlayModalOnModalPinia = ref<boolean>(false)
  const hideFeature = ref<boolean>(false)
  const ticketsList = ref<Ticket[]>([])
  const connectionPopup = ref<boolean>(false)

  const newOldFeatureMap: Record<string, OldFeatureType> = {
    info: 'featureInfo',
    attachments: 'featureAttachments',
    analytics: 'featureAnalytic',
    wip: 'featureWip',
    'project-analysis': 'featureProjectAnalysis'
  }

  const oldNewFeatureMap: Record<OldFeatureType, string | null> = {
    featureInfo: 'info',
    featureAttachments: 'attachments',
    featureAnalytic: 'analytics',
    featureProjectAnalysis: 'project-analysis',
    featureWip: 'wip',
    '': null
  }

  // Getters
  const selectedAttachment = computed(() => {
    return selectedFile.value ?? selectedLink.value
  })
  const showFeature = computed(() => {
    return mainFeatureName.value ? newOldFeatureMap[mainFeatureName.value] : ''
  })
  const showFeatureAnalytic = computed(() => {
    return ['analytics', 'assets', 'wip'].includes(mainFeatureName.value)
  })
  const showFeatureProjectAnalysis = computed(() => {
    return mainFeatureName.value === 'project-analysis'
  })
  const showFeatureAttachments = computed(() => {
    return mainFeatureName.value === 'attachments'
  })
  const showFeatureErrands = computed(() => {
    return mainFeatureName.value === 'errands'
  })
  const showFeatureInfo = computed(() => {
    return mainFeatureName.value === 'info'
  })
  const showNoFeature = computed(() => {
    return hideFeature.value
  })
  const showConnectionPopup = computed(() => {
    return connectionPopup.value
  })
  // Actions
  function setCurrentOverlayModalPinia(modalName: string) {
    currentOverlayModalPinia.value = modalName
  }
  function setCurrentOverlayModalOnModalPinia(modalName: string) {
    currentOverlayModalOnModalPinia.value = modalName
  }
  function setFilesList(filesArray: Attachment[]) {
    filesList.value = filesArray
  }
  function setFilesUploaded(filesArray: string[] | null) {
    if (!filesArray) {
      filesUploaded.value = []
      return
    }
    if (filesArray.length) filesUploaded.value = filesArray
  }
  function setFilteredDrawings(drawings: Drawing[]) {
    filteredDrawings.value = drawings
  }
  function setFilteredLines(lines: Line[]) {
    filteredLineLayers.value = lines
  }
  function setFilteredOrthophotos(orthophotos: Orthophoto[]) {
    filteredOrthophotos.value = orthophotos
  }
  function setFilteredWmsLayers(wmss: any[]) {
    filteredWmsLayers.value = wmss
  }
  function setIsFeatureFullscreen(isFullscreen: boolean) {
    isFeatureFullscreen.value = isFullscreen
  }
  function setLineCategoriesForRealEstate(categories: LineCategoryDto[]) {
    lineCategoriesForSelectedRealEstate.value = categories
  }
  function setLineDataForCoreEntity(lines: LineGeoJsonFeature[]) {
    linesForSelectedRealEstate.value = lines
  }
  function setLinksList(linksArray: Attachment[]) {
    linksList.value = linksArray
  }
  // Used to trigger watch, doesn't modify a list itself
  async function setLinksUpdated(val: Attachment) {
    const links = [...linksUpdated.value]
    links.push(val.attachmentId)
    linksUpdated.value = links
  }
  function setRightClickCoordinate(coordinate: Array<number>) {
    rightClickCoordinate.value = coordinate
  }
  function setSelectedAnalysisKPI(kpi: string | undefined) {
    selectedAnalysisKPI.value = kpi
  }
  function setSelectedFile(file: Attachment | null) {
    selectedFile.value = file
  }
  function setSelectedLink(link: Attachment | null) {
    selectedLink.value = link
  }
  function setSelectedLine(line: LineGeoJsonProperties | null) {
    selectedLine.value = line
    selectedLineLayerId.value = line?.layerId ?? null
  }
  function setSelectedTicket(ticket: Ticket | null) {
    selectedTicket.value = ticket
  }
  function setSelectedZone(zone: ZoneProperties | null) {
    selectedZone.value = zone
    selectedZoneLayerId.value = zone?.layerId ?? null
  }
  function setShowLineMenu(showMenu: boolean) {
    showLineMenu.value = showMenu
  }
  function setShowOverlayModalPinia(showModal: boolean) {
    showOverlayModalPinia.value = showModal
  }
  function setShowOverlayModalOnModalPinia(showModal: boolean) {
    showOverlayModalOnModalPinia.value = showModal
  }
  function setShowFeature(showFeatureName: OldFeatureType) {
    const feature = oldNewFeatureMap[showFeatureName as OldFeatureType]
    if (feature) setMainFeature(feature)
  }
  function setTicketsList(ticketsArray: Ticket[]) {
    ticketsList.value = ticketsArray
  }
  function setShowNoFeature(active: boolean) {
    hideFeature.value = active
  }
  function setShowConnectionPopup(active: boolean) {
    connectionPopup.value = active
  }
  async function updateLineDataForCoreEntity(
    categoryId: string,
    typeId: string,
    coreId: string,
    checked: boolean
  ) {
    const filteredLines = [...filteredLineLayers.value]
    const isTypeDataAlreadyFetched = linesForSelectedRealEstate.value.some(
      (feature) => feature.properties.typeId === typeId
    )
    if (!isTypeDataAlreadyFetched) {
      const newLines = await addLineFeaturesForType(
        coreId,
        categoryId,
        typeId,
        linesForSelectedRealEstate.value
      )
      if (!newLines?.length) return
      setLineDataForCoreEntity(newLines)
      setNewLineDataVisibility(categoryId, typeId, checked, filteredLines)
    } else {
      setExistingLineDataVisibility(typeId, checked, filteredLines)
    }
    setFilteredLines(filteredLines)
  }
  return {
    currentOverlayModalPinia,
    currentOverlayModalOnModalPinia,
    filesList,
    filesUploaded,
    filteredDrawings,
    filteredLineLayers,
    filteredOrthophotos,
    filteredWmsLayers,
    isFeatureFullscreen,
    linesForSelectedRealEstate,
    lineCategoriesForSelectedRealEstate,
    linksList,
    linksUpdated,
    rightClickCoordinate,
    selectedAnalysisKPI,
    selectedAttachment,
    selectedFile,
    selectedLink,
    selectedLine,
    selectedLineLayerId,
    selectedTicket,
    selectedZone,
    selectedZoneLayerId,
    setCurrentOverlayModalPinia,
    setCurrentOverlayModalOnModalPinia,
    setFilesList,
    setFilesUploaded,
    setFilteredDrawings,
    setFilteredLines,
    setFilteredOrthophotos,
    setFilteredWmsLayers,
    setIsFeatureFullscreen,
    setLineCategoriesForRealEstate,
    setLineDataForCoreEntity,
    setLinksList,
    setLinksUpdated,
    setRightClickCoordinate,
    setSelectedAnalysisKPI,
    setSelectedFile,
    setSelectedLink,
    setSelectedLine,
    setSelectedTicket,
    setSelectedZone,
    setShowLineMenu,
    setShowOverlayModalPinia,
    setShowOverlayModalOnModalPinia,
    setShowFeature,
    setShowNoFeature,
    setTicketsList,
    setShowConnectionPopup,
    showConnectionPopup,
    showFeature,
    showFeatureAnalytic,
    showFeatureAttachments,
    showFeatureProjectAnalysis,
    showFeatureErrands,
    showFeatureInfo,
    showLineMenu,
    showNoFeature,
    showOverlayModalPinia,
    showOverlayModalOnModalPinia,
    ticketsList,
    updateLineDataForCoreEntity
  }
})
